import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiShield, FiUserPlus, FiEdit, FiTrash2, FiSave, FiX, FiArchive, FiRefreshCw, FiEye, FiEyeOff } = FiIcons;

const ManageCoaches = () => {
  const { coaches, createCoach, updateCoach, user, archiveCoach, restoreCoach, showArchived, toggleShowArchived } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [coachToAction, setCoachToAction] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    role: 'Coach',
    password: ''
  });

  const handleOpenModal = (coach = null) => {
    if (coach) {
      setEditingCoach(coach);
      setFormData({
        name: coach.name,
        username: coach.username,
        email: coach.email,
        role: coach.role,
        password: ''
      });
    } else {
      setEditingCoach(null);
      setFormData({
        name: '',
        username: '',
        email: '',
        role: 'Coach',
        password: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCoach) {
        await updateCoach({ id: editingCoach.id, ...formData });
      } else {
        if (!formData.password) {
          alert('Password is required for new coaches');
          return;
        }
        await createCoach(formData);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving coach:', error);
      alert('Failed to save coach. Please try again.');
    }
  };

  // Handle Archive
  const handleArchiveClick = (coach) => {
    if (coach.id === user.id) {
      alert("You cannot archive your own account.");
      return;
    }
    setCoachToAction(coach);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (coachToAction) {
      await archiveCoach(coachToAction.id);
      setArchiveModalOpen(false);
      setCoachToAction(null);
    }
  };

  // Handle Restore
  const handleRestoreClick = (coach) => {
    setCoachToAction(coach);
    setRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (coachToAction) {
      await restoreCoach(coachToAction.id);
      setRestoreModalOpen(false);
      setCoachToAction(null);
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Coaches</h1>
            <p className="text-gray-600">Add and manage coach accounts</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={toggleShowArchived}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={showArchived ? FiEyeOff : FiEye} className="w-4 h-4" />
              <span>{showArchived ? "Hide Archived" : "Show Archived"}</span>
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
              <span>Add New Coach</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coach</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coaches.map((coach) => (
                  <tr key={coach.id} className={`hover:bg-gray-50 ${coach.is_archived ? 'bg-gray-100' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {coach.name}
                            {coach.id === user.id && (
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">You</span>
                            )}
                            {coach.is_archived && (
                              <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">Archived</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                            {coach.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <SafeIcon icon={FiShield} className="w-4 h-4 mr-1 text-gray-400" />
                        {coach.role}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coach.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        {!coach.is_archived ? (
                          <>
                            <button
                              onClick={() => handleOpenModal(coach)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            {coach.id !== user.id && (
                              <button
                                onClick={() => handleArchiveClick(coach)}
                                className="text-amber-600 hover:text-amber-900"
                                title="Archive coach"
                              >
                                <SafeIcon icon={FiArchive} className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => handleRestoreClick(coach)}
                            className="text-green-600 hover:text-green-900"
                            title="Restore coach"
                          >
                            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingCoach ? 'Edit Coach' : 'Add New Coach'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role / Specialization</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingCoach ? 'New Password (leave blank to keep current)' : 'Password'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    {...(!editingCoach ? { required: true } : {})}
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>Save Coach</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Archive Confirmation Modal */}
      <AnimatePresence>
        {archiveModalOpen && coachToAction && (
          <DeleteConfirmModal
            title="Archive Coach"
            message={`Are you sure you want to archive ${coachToAction.name}? They will no longer be able to log in, but their data (notes, member assignments) will be preserved.`}
            confirmText="Archive"
            confirmIcon={FiArchive}
            confirmColor="bg-amber-600 hover:bg-amber-700"
            onConfirm={handleConfirmArchive}
            onCancel={() => {
              setArchiveModalOpen(false);
              setCoachToAction(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {restoreModalOpen && coachToAction && (
          <DeleteConfirmModal
            title="Restore Coach"
            message={`Are you sure you want to restore ${coachToAction.name}? They will be able to log in again immediately.`}
            confirmText="Restore"
            confirmIcon={FiRefreshCw}
            confirmColor="bg-green-600 hover:bg-green-700"
            onConfirm={handleConfirmRestore}
            onCancel={() => {
              setRestoreModalOpen(false);
              setCoachToAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManageCoaches;